'use client'

import { useEffect, useRef, useState, type PropsWithChildren } from 'react'
import { usePathname } from 'next/navigation'

import packageJson from '../../../package.json'
import { createAnalyticsLifecycle } from './analytics-lifecycle'
import {
  clearBrowserAuthMethodMarker,
  consumeBrowserAuthMethodMarker,
  getAuthMethodMarkerAction,
  writeBrowserAuthMethodMarker,
  type BrowserAuthMethodMarkerConsumption,
} from './auth-method-marker'
import { type AuthMethod } from './events'
import { isProductAnalyticsConfigured, productAnalytics, type ProductAnalyticsConfig } from './product-analytics'

interface PostHogProviderProps extends PropsWithChildren {
  isImpersonating: boolean
  userId?: string
}

interface SyncPostHogProviderAnalyticsOptions {
  analyticsLifecycle: ReturnType<typeof createAnalyticsLifecycle>
  clearAuthMethod(): boolean
  config: ProductAnalyticsConfig
  consumeAuthMethod(): BrowserAuthMethodMarkerConsumption
  isImpersonating: boolean
  pathname?: string
  restoreAuthMethod(authMethod: AuthMethod): boolean
  userId?: string
}

type PostHogProviderAnalyticsSyncResult = 'complete' | 'disabled' | 'retry'

const POSTHOG_PROVIDER_RETRY_DELAY_MS = 1000
const POSTHOG_PROVIDER_MAX_RETRY_DELAY_MS = 30000

export const schedulePostHogProviderAnalyticsRetry = (retry: () => void, attempt: number): (() => void) => {
  const delay = Math.min(POSTHOG_PROVIDER_RETRY_DELAY_MS * 2 ** attempt, POSTHOG_PROVIDER_MAX_RETRY_DELAY_MS)
  const timeoutId = setTimeout(retry, delay)

  return () => clearTimeout(timeoutId)
}

export const syncPostHogProviderAnalytics = ({
  analyticsLifecycle,
  clearAuthMethod,
  config,
  consumeAuthMethod,
  isImpersonating,
  pathname,
  restoreAuthMethod,
  userId,
}: SyncPostHogProviderAnalyticsOptions): PostHogProviderAnalyticsSyncResult => {
  if (!isProductAnalyticsConfigured(config)) {
    const syncAction = analyticsLifecycle.sync({ config, pathname, userId })
    return syncAction === 'disabled' ? 'disabled' : 'retry'
  }

  const markerAction = getAuthMethodMarkerAction({ isImpersonating, userId })
  let authMethod: AuthMethod | undefined
  if (markerAction === 'consume') {
    let marker: BrowserAuthMethodMarkerConsumption
    try {
      marker = consumeAuthMethod()
    } catch {
      analyticsLifecycle.suspendIdentity()
      return 'retry'
    }
    if (!marker.isKnown) {
      analyticsLifecycle.suspendIdentity()
      return 'retry'
    }
    authMethod = marker.authMethod
  } else if (markerAction === 'discard') {
    try {
      clearAuthMethod()
    } catch {
      // The analytics marker must not affect application rendering.
    }
  }

  const syncAction = analyticsLifecycle.sync({ authMethod, config, pathname, userId })
  if (syncAction === 'retry') {
    analyticsLifecycle.suspendIdentity()
    if (authMethod) {
      try {
        restoreAuthMethod(authMethod)
      } catch {
        // The analytics marker must not affect application rendering.
      }
    }
    return 'retry'
  }

  return 'complete'
}

export const PostHogProvider = ({ children, isImpersonating, userId }: PostHogProviderProps) => {
  const pathname = usePathname()
  const lifecycle = useRef(createAnalyticsLifecycle(productAnalytics))
  const retryAttempt = useRef(0)
  const [retryVersion, setRetryVersion] = useState(0)

  useEffect(() => {
    retryAttempt.current = 0
  }, [isImpersonating, pathname, userId])

  useEffect(() => {
    const syncResult = syncPostHogProviderAnalytics({
      analyticsLifecycle: lifecycle.current,
      clearAuthMethod: clearBrowserAuthMethodMarker,
      config: {
        appVersion: packageJson.version,
        enabled: process.env.NEXT_PUBLIC_POSTHOG_ENABLED === 'true',
        environment: process.env.NODE_ENV === 'production' ? 'production' : 'development',
        errorReportingEnabled: process.env.NODE_ENV !== 'development' && Boolean(process.env.NEXT_PUBLIC_SENTRY_DSN),
        host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? '',
        isImpersonating,
        projectToken: process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN ?? '',
      },
      consumeAuthMethod: consumeBrowserAuthMethodMarker,
      isImpersonating,
      pathname,
      restoreAuthMethod: writeBrowserAuthMethodMarker,
      userId,
    })
    if (syncResult === 'retry') {
      const currentAttempt = retryAttempt.current
      retryAttempt.current += 1
      return schedulePostHogProviderAnalyticsRetry(() => setRetryVersion((version) => version + 1), currentAttempt)
    }
    retryAttempt.current = 0
  }, [isImpersonating, pathname, retryVersion, userId])

  return children
}
